<template>
	<div v-loading="loading" class="lcsj-table-container">
		<el-table
			ref="tableData"
			class="lcsj-data-table"
			:row-key="rowKey"
			:border="border"
			:stripe="stripe"
			:max-height="maxHeight || undefined"
			:height="fittedHeight !== null ? fittedHeight : ((tableType === 'normal' ? height : dialogHeight) || undefined)"
			:size="tableSize"
			:data="data"
			:load="loadNode"
			:lazy="lazy"
			:tree-props="treeProps"
			v-bind="otherConfig"
			:default-expand-all="expandAll"
			:highlight-current-row="highlight"
			:header-cell-style="headerCellStyle"
			v-if="!isChange"
			@selection-change="handleSelectionChange"
			@current-change="handleTableCurrentChange"
			@row-click="handleTableRowClick"
			@sort-change="handleSortChange"
			@cell-click="handleCellChange"
			@cell-dblclick="handleDBCellChange"
			@row-dblclick="handleRowDBClick"
			:cell-class-name="moneyCellClass"
			v-on="$listeners"
		>
			<template v-for="(item, index) in displayColumns">
				<!-- 选择框 -->
				<el-table-column
					v-if="item.selection"
					:key="`selection_${index}`"
					type="selection"
					width="50"
					:fixed="item.fixed"
					:reserve-selection="reserveSelection"
					align="center"
					header-align="center"
				></el-table-column>
				<!-- 自定义单选框 -->
				<el-table-column v-else-if="item.singleSelection" :key="`singleSelection_${index}`" width="50" :fixed="item.fixed" align="center" header-align="center">
					<template #default="scope">
						<el-checkbox v-model="scope.row.isSingleSelected" @change="handleSingleSelectionChange(scope.row)"></el-checkbox>
					</template>
				</el-table-column>
				<!-- 序号 -->
				<el-table-column
					v-else-if="item.index"
					:key="`index_${index}`"
					type="index"
					width="60"
					:fixed="item.fixed"
					label="序号"
					align="center"
					:index="item.indexMethod || indexMethod"
				></el-table-column>

				<!-- 操作 -->
				<el-table-column v-else-if="item.operation" :key="`operation_${index}`" label="操作" :width="item.operationWidth || 260" :fixed="item.fixed" align="center">
					<template #default="scope">
						<div class="lcsj-row-actions">
							<button
								v-if="item.btnGroup[0]"
								class="lcsj-row-actions__link"
								:class="{ 'lcsj-row-actions__link--danger': item.btnGroup[0].btnType === 'danger' }"
								@click.stop="
									handleCommand({
										rowData: scope.row,
										command: item.btnGroup[0].type
									})
								"
							>
								{{ item.btnGroup[0].name }}
							</button>
							<button
								v-if="item.btnGroup[1]"
								class="lcsj-row-actions__link"
								:class="{ 'lcsj-row-actions__link--danger': item.btnGroup[1].btnType === 'danger' }"
								@click.stop="
									handleCommand({
										rowData: scope.row,
										command: item.btnGroup[1].type
									})
								"
							>
								{{ item.btnGroup[1].name }}
							</button>
							<button
								v-if="item.btnGroup[2] && item.btnGroup.length <= 3"
								class="lcsj-row-actions__link"
								:class="{ 'lcsj-row-actions__link--danger': item.btnGroup[2].btnType === 'danger' }"
								@click.stop="
									handleCommand({
										rowData: scope.row,
										command: item.btnGroup[2].type
									})
								"
							>
								{{ item.btnGroup[2].name }}
							</button>
							<el-dropdown v-if="item.btnGroup.length > 3" trigger="click" placement="top" @command="handleCommand">
								<button class="lcsj-row-actions__link">更多<i class="el-icon-arrow-down el-icon--right"></i></button>
								<template #dropdown>
									<el-dropdown-menu style="float: right; padding: 3px 0">
										<el-dropdown-item
											v-for="(list, listIndex) in item.btnGroup"
											v-show="listIndex !== 0 && listIndex !== 1"
											:key="list.type"
											:icon="list.icon"
											:command="beforeHandleCommand(scope.row, list.type)"
										>
											{{ list.name }}
										</el-dropdown-item>
									</el-dropdown-menu>
								</template>
							</el-dropdown>
						</div>
					</template>
				</el-table-column>
				<el-table-column v-else-if="item.operationC" :key="`operation1_${index}`" label="操作" width="80" fixed="right" align="center">
					<template #default="scope">
						<button class="lcsj-row-actions__link" @click.stop="handleOperation(scope.row)">{{ item.label }}</button>
					</template>
				</el-table-column>
				<!-- 多级表头 -->
				<el-table-column v-else-if="item.multi" :key="`multi_${index}`" align="center" :label="item.label">
					<template v-for="(child, childIndex) in item.children">
						<el-table-column v-if="child.multi" :key="`childMulti_${index}_${childIndex}`" align="center" :label="child.label">
							<template>
								<el-table-column v-for="(grandson, grandsonIndex) in child.children" :key="`child_${index}_${childIndex}_${grandsonIndex}`" show-overflow-tooltip v-bind="grandson"></el-table-column>
							</template>
						</el-table-column>
						<slot v-else-if="child.slot" :name="child.slot"></slot>
						<el-table-column v-else v-bind="child" :key="`child_${index}_${childIndex}`" show-overflow-tooltip></el-table-column>
					</template>
				</el-table-column>
				<!-- 自定义内容 -->
				<slot v-else-if="item.slot" :name="item.slot"></slot>
				<!-- 金额字段：带颜色 + ￥ + 元，列宽自适应不省略 -->
				<el-table-column
					v-else-if="item.isMoney"
					v-bind="item"
					:key="`money_${index}`"
					:show-overflow-tooltip="false"
					:width="undefined"
					:min-width="item.minWidth || item.width || guessWidth(item.label) || 120"
					:fixed="item.fixed"
				>
					<template #default="scope">
						<span class="money-cell" :style="{ color: moneyColor(item, scope.row) }">
							{{ formatMoney(getCellValue(scope.row, item.prop)) }}
						</span>
					</template>
				</el-table-column>
				<!-- 常规字段：列宽兜底 min-width，避免长列头（如"最后一次赠送金额"）被挤到换行。
     min-width = 显式 minWidth > 按 label 文本估算 > 默认 100 -->
				<el-table-column
					v-else
					v-bind="item"
					:key="`normal_${index}`"
					:show-overflow-tooltip="!item.noTooltip"
					:fixed="item.fixed"
					:min-width="item.minWidth || item.width || guessWidth(item.label)"
				></el-table-column>
			</template>

			<!-- 空状态：统一由设计系统提供（图标 + 文案）。
			     Element 的 empty 插槽是纯文本节点，这里换成结构化内容；
			     视觉细节见 assets/scss/theme.scss 的 .el-table__empty-text。
			     页面若需自定义，传 empty-text 即可覆盖文案。 -->
			<template #empty>
				<div class="lcsj-table-empty">
					<i class="el-icon-document-delete lcsj-table-empty__icon"></i>
					<span class="lcsj-table-empty__text">{{ emptyText === false ? "" : emptyText }}</span>
					<slot name="emptyExtra"></slot>
				</div>
			</template>
		</el-table>
	</div>
</template>
<script>
import { MyDebounce } from "@/utils/util.js";
import { isNegativeMoney, moneyCellClass as moneyCellClassUtil, MONEY_NEGATIVE_COLOR } from "@/utils/money.js";
export default {
	name: "STable",
	props: {
		columns: {
			type: Array,
			default: () => []
		},
		data: {
			type: Array,
			default: () => []
		},
		emptyText: {
			/** 空状态文案；传false 可隐藏默认文案 */
			type: [String, Boolean],
			default: "暂无数据"
		},
		tableType: {
			type: String,
			default: "normal"
		},
		maxHeight: {
			type: [Number, String],
			default: ""
		},
		dialogHeight: {
			type: [Number, String],
			default: ""
		},
		height: {
			type: [Number, String],
			default: ""
		},
		tableSize: {
			type: String,
			default: "small"
		},
		stripe: {
			type: Boolean,
			default: true
		},
		border: {
			type: Boolean,
			default: true
		},
		lazy: {
			type: Boolean,
			default: false
		},
		expandAll: {
			type: Boolean,
			default: false
		},
		treeProps: {
			type: Object,
			default: () => {}
		},
		otherConfig: {
			type: Object,
			default: () => {}
		},
		loading: {
			type: Boolean,
			default: false
		},
		// 支持跨页选择的两个配置项
		rowKey: {
			type: String,
			default: ""
		},
		reserveSelection: {
			type: Boolean,
			default: false
		},
		highlight: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			fittedHeight: null,
			isChange: false
		};
	},
	computed: {
		// 统一取消左侧固定列：所有列表页的 fixed: 'left' 在此剥离（仅取消固定，列本身保留；右固定不受影响）
		// 含多级表头 children / 孙级；返回浅拷贝，不改动页面传入的原始 columns
		displayColumns() {
			const stripLeft = (col) => {
				if (col && String(col.fixed).toLowerCase() === "left") {
					const next = { ...col };
					delete next.fixed;
					return next;
				}
				return col;
			};
			return this.columns.map((item) => {
				let next = stripLeft(item);
				if (next.multi && Array.isArray(next.children)) {
					next = {
						...next,
						children: next.children.map((child) => {
							let c = stripLeft(child);
							if (c.multi && Array.isArray(c.children)) {
								c = { ...c, children: c.children.map(stripLeft) };
							}
							return c;
						})
					};
				}
				return next;
			});
		}
	},
	activated() {
		this.doLayoutTable();
	},
	mounted() {
		window.addEventListener("resize", this.handleWindowResize);
		if (typeof ResizeObserver !== "undefined") {
			this.fitObserver = new ResizeObserver(this.updateFittedHeight);
			this.fitObserver.observe(this.$el);
		}
		this.updateFittedHeight();
	},
	beforeDestroy() {
		window.removeEventListener("resize", this.handleWindowResize);
		if (this.fitObserver) this.fitObserver.disconnect();
	},
	methods: {
		updateFittedHeight() {
			const parent = this.$el && this.$el.parentElement;
			const fits = parent && parent.classList.contains("pd20--paged-table");
			this.fittedHeight = fits ? Math.max(160, Math.floor(this.$el.getBoundingClientRect().height)) : null;
		},
		/**
		 * 按列头文字估算列宽（像素）：
		 * 中文/全角按 14px、半角按 7.5px 估算，另加 24px 内边距与余量。
		 * 用于给未显式设宽度的列兜底，避免长列头被压到换行。
		 * 超过 8 个全角字符时不再放大，避免超长列头（如"①创建订单总数量"）把表格撑得过宽。
		 */
		guessWidth(label) {
			if (!label) return 0;
			const text = String(label);
			let w = 0;
			for (const ch of text) {
				w += ch.charCodeAt(0) > 127 ? 14 : 7.5;
			}
			const full = text.length;
			const width = Math.ceil(w + 24 + 8);
			// 上限 180：兼顾不换行与整表宽度
			return Math.min(Math.max(width, 70), full > 8 ? 140 : 180);
		},
		// 支持 a.b.c 形式的嵌套取值
		getCellValue(row, prop) {
			if (!prop) return "";
			return prop.split(".").reduce((obj, key) => (obj == null ? undefined : obj[key]), row);
		},
		// 金额格式化：￥xxx.xx 元
		formatMoney(val) {
			if (val === null || val === undefined || val === "") return "￥0.00 元";
			const num = Number(val);
			if (isNaN(num)) return val;
			return "￥" + num.toFixed(2) + " 元";
		},
		// 金额颜色：负金额一律红色（最高优先级），其余按语义 income-绿 / expense-红 / normal-橙
		moneyColor(item, row) {
			if (isNegativeMoney(this.getCellValue(row, item.prop))) return MONEY_NEGATIVE_COLOR;
			let type = item.moneyType || "normal";
			// 支持按行动态判断（传函数）
			if (typeof item.moneyType === "function") {
				type = item.moneyType(row);
			}
			if (type === "income") return "var(--app-success-text)";
			if (type === "expense") return "var(--app-danger-text)";
			return "var(--app-warning-text)";
		},
		// 金额列负数标红：覆盖常规列、slot 自定义列、多级表头与汇总行
		moneyCellClass(scope) {
			return moneyCellClassUtil(scope);
		},
		handleOperation(row) {
			// Vue2 自定义事件名区分大小写：同时派发 camel 与 kebab，兼容所有父级监听写法
			// （handleOperation / handle-operation 均为自定义名，不会重复触发）
			this.$emit("handleOperation", row);
			this.$emit("handle-operation", row);
		},
		handleSet(a, b) {},
		handleCellChange(row, column, rowIndex, columnIndex) {
			// this.$emit("cell-click", row, column, rowIndex, columnIndex);
		},
		handleDBCellChange(row, column, rowIndex, columnIndex) {
			this.$emit("cell-dbclick", row, column, rowIndex, columnIndex);
		},
		// 切换选择
		handleSelectionChange(val) {
			this.$emit("changeSelection", val);
		},
		// 单选
		handleTableCurrentChange(currentRow) {
			this.$emit("changeCurrent", currentRow);
		},
		// 自定义单选
		handleSingleSelectionChange(currentRow) {
			this.$emit("changeSingleSelection", currentRow);
		},
		// 点击行
		handleTableRowClick(currentRow, currentColumn, currentEvent) {
			this.$emit("rowClick", currentRow, currentColumn, currentEvent);
		},

		// 双击行
		handleRowDBClick(currentRow) {
			// Vue2 事件名区分大小写：同时派发 kebab 与 camel，兼容所有父级监听写法
			// （row-db-click / rowDBClick 均为自定义名，el-table 原生名是 row-dblclick，不会重复触发）
			this.$emit("row-db-click", currentRow);
			this.$emit("rowDBClick", currentRow);
		},

		// 懒加载子表
		// Vue2 自定义事件名严格按字符串匹配，不做驼峰/短横线互转：
		// 这里派发 kebab-case 的 load-children，与父级 @load-children 监听匹配，
		// 否则父级方法不触发、resolve 不执行，会导致展开箭头一直转圈、子列表不显示。
		loadNode(row, treeNode, resolve) {
			this.$emit("load-children", row, treeNode, resolve);
		},

		// 排序
		handleSortChange(val) {
			this.$emit("changeSort", val);
		},
		// 序号列自定义方法
		indexMethod(index) {
			return index + 1;
		},
		// 页面缩放重新计算 table高度
		handleWindowResize() {
			this.$emit("windowResize");
			this.resizeTable();
		},
		// 选项操作按钮
		handleCommand(command) {
			this.$emit("handle-command", command);
		},
		headerCellStyle() {
			// 走设计 token：换主题时表头色自动跟随
			return {
				background: "var(--app-table-header-bg)",
				color: "var(--app-text)",
				height: "44px",
				fontWeight: 600
			};
		},

		// 对table重新布局
		doLayoutTable() {
			if (this.$refs.tableData && this.$refs.tableData.doLayout) {
				this.$refs.tableData.doLayout();
				this.resizeTable();
			}
		},

		beforeHandleCommand(item, command) {
			return {
				rowData: item,
				command: command
			};
		},
		// 判断是否超过可视区域 及有无滚动条
		resizeTable: MyDebounce(function () {
			if (!this.$refs.tableData || !this.$refs.tableData.bodyWidth) return;
			if (Number(this.$refs.tableData.bodyWidth.split("px")[0]) > this.$refs.tableData.$el.clientWidth) {
				this.$refs.tableData.$el.classList.add("fixed-table");
			} else {
				this.$refs.tableData.$el.classList.remove("fixed-table");
			}
		}, 100)
	},
	watch: {
		columns: {
			handler(newVal) {
				if (newVal && newVal.length) {
					this.isChange = true;
					this.$nextTick(() => {
						this.isChange = false;
						setTimeout(() => {
							if (this.$refs.tableData && this.$refs.tableData.doLayout) {
								this.$refs.tableData.doLayout();
								this.resizeTable();
							}
						}, 300);
					});
				}
			},
			deep: true
		},
		data: {
			handler(newVal) {
				if (newVal && newVal.length) {
					this.$nextTick(() => {
						setTimeout(() => {
							this.$refs.tableData.doLayout();
							this.resizeTable();
						}, 300);
					});
				}
			},
			deep: true
		}
	}
};
</script>
<style scoped lang="scss">
/* 选中行：用浅底+ 主色文字，而非「主色底+ 白字」。
   原因：白字写法在深色主题下 --app-surface 变深，会出现深底深字不可读。 */
::v-deep .el-table__body tr.current-row > td {
	background: var(--app-surface-selected) !important;
	color: var(--app-text) !important;
}

::v-deep .el-table__body tr.current-row > td .lcsj-row-actions__link {
	color: var(--app-primary-text) !important;
}

.money-cell {
	font-weight: 600;
	white-space: nowrap;
}
</style>
